


function extractNumericPhoneValueWithoutUSCountryCode(input) {
    // Remove non-numeric characters
    let digits = input.replace(/\D/g, "");

    // Remove the first character if it's '1'
    if (digits.startsWith("1")) {
        digits = digits.slice(1);
    }

    // Return the first 10 digits
    return digits.slice(0, 10);
}

/**
 * KBMaxEmbed class to handle all KBMax configurator operations
 */
class KBMaxEmbed {
    constructor(configurator) {
        this._configurator = configurator;
    }

    /**
     * Get the underlying configurator instance
     */
    get configurator() {
        return this._configurator;
    }

    /**
     * Attach a message handler to the configurator
     * @param {Function} callback - Callback function to handle messages
     */
    attachMessageHandler(callback) {
        if (this._configurator && callback) {
            this._configurator.onMessage.add(msg => {
                if (msg.name == "ConfigToEmbed") {
                    if (import.meta.env.MODE === 'development') {
                        console.log("kbglue: ConfigToEmbed message received: ", msg);
                    }
                    callback(msg.data, msg);
                }
            });
        }
    }

    /**
     * Set default user fields in the configurator
     * @param {Object} user - User object with profile information
     */
    setDefaultUserFields(user) {
        this._configurator.setFields({ "Field - User Email": user.Email });
        this._configurator.setFields({ "User Email - Customer Facing": user.Email });
        this._configurator.setFields({ "User Name - Customer Facing": `${user.FirstName} ${user.LastName}` });
        this._configurator.setFields({ "User Role - Customer Facing": user.UserType });
        this._configurator.setFields({ "User Company - Customer Facing": user.Company });
        if (user.Phone) {
            this._configurator.setFields({ "User Phone - Customer Facing": extractNumericPhoneValueWithoutUSCountryCode(user.Phone) });
        }
    }

    /**
     * Get the configured product asynchronously
     * @returns {Promise} Promise resolving to the configured product
     */
    getConfiguredProductAsync() {
        return new Promise((resolve, reject) => {
            try {
                this._configurator.getConfiguredProduct((cp) => {
                    resolve(cp);
                });
            } catch (err) {
                reject(err);
            }
        });
    }

    getFieldsAsync() {
        return new Promise((resolve, reject) => {
            try {
                this._configurator.getFields((flds) => {
                    resolve(flds);
                });
            } catch (err) {
                reject(err);
            }
        });
    }

    /**
     * Submit the project to the specified endpoint
     * @param {string} endpoint - API endpoint to submit to
     * @param {string} quoteId - Quote ID
     * @param {string} toolName - Tool name
     * @param {string} pardotEndpointUrl - Pardot endpoint URL
     * @param {string} salesForceCampignId - Salesforce campaign ID
     * @returns {Promise} Promise resolving to the result of the submission
     */
    async submitProject(endpoint, quoteId, toolName, pardotEndpointUrl, salesForceCampignId) {
        let configuredProduct = null;
        try {
            configuredProduct = await this.getConfiguredProductAsync();
        } catch (error) {
            return {
                success: false,
                error: "Failed to get configured product",
                details: error
            };
        }

        const formData = new FormData();

        //basic data
        formData.append("configuredProduct", JSON.stringify(configuredProduct));
        formData.append("quoteId", quoteId);
        formData.append("toolName", toolName);

        // Tracking data
        if (pardotEndpointUrl)
            formData.append("submitTrackingPardotEndpointUrl", pardotEndpointUrl);
        if (salesForceCampignId)
            formData.append("submitTrackingSalesforceCampaignId", salesForceCampignId);

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                body: formData,
            });

            const contentType = response.headers.get("content-type");
            let result = {};

            if (contentType && contentType.includes("application/json")) {
                result = await response.json();
            } else {
                result = {
                    success: response.ok,
                    responseText: await response.text()
                };
            }

            if (result.success) {
                result.configuredProduct = configuredProduct;
            }

            return result;

        } catch (error) {
            console.error("Error contacting server", error);
            return {
                success: false,
                responseText: "Error contacting server.",
                error: error
            };
        }
    }



    async saveProject(endpoint, quoteId, toolName, saveAsNewQuote) {
        try {

            let configuredProduct = null;
            try {
                configuredProduct = await this.getConfiguredProductAsync();
            } catch (error) {
                return {
                    success: false,
                    error: "Failed to get configured product",
                    details: error
                };
            }

            const formData = new FormData();
            formData.append("configuredProduct", JSON.stringify(configuredProduct));
            formData.append("quoteId", quoteId);
            formData.append("saveAsNewQuote", saveAsNewQuote);
            formData.append("toolName", toolName);

            const response = await fetch(endpoint, {
                method: "POST",
                body: formData,
            });

            const contentType = response.headers.get("content-type");
            let result = {};

            if (contentType && contentType.includes("application/json")) {
                result = await response.json();
            } else {
                result = {
                    success: response.ok,
                    responseText: await response.text()
                };
            }

            return result;

        } catch (error) {
            return {
                success: false,
                responseText: "Error contacting server.",
                error: error
            };
        }
    }

    /**
     * Dispose of the configurator and clean up resources
     */
    dispose() {
        if (this._configurator) {
            this._configurator.dispose();
            this._configurator = null;
        }
    }
}

const kbmax = window.kbmax;
let module = {};
if (kbmax) {
    module = {        /**
         * Factory method to create a KBMaxEmbed instance
         * @param {string} kbMaxUrl - KBMax URL
         * @param {string} configuratorId - Configurator ID
         * @param {Object} configuredProductObj - Configured product object
         * @param {string} mountingElementId - ID of the element to mount the configurator
         * @returns {KBMaxEmbed} A new KBMaxEmbed instance
         */
        createConfigurator(kbMaxUrl, configuratorId, configuredProductObj, mountingElementId) {
            const configurator = new kbmax.ConfiguratorEmbed({
                kbmaxUrl: kbMaxUrl,
                configuratorId: configuratorId,
                configuredProduct: configuredProductObj,
                elementId: mountingElementId,
                showHeader: false,
                showDrawer: false,
                showMove: false,
                bindToFormSelector: "",
                loadStyle: "none"
            });

            //set standard fields
            configurator.setFields({ "IsWebUser": "True" });

            //development logging
            if (import.meta.env.MODE === 'development') {
                console.log("kbglue: createConfigurator called and ran");

                configurator.getFields(function (o) {
                    console.log("kbglue: field names in this embedded configurator: ", o);
                });
            }

            return new KBMaxEmbed(configurator);
        }
    };
}

export default module;


